/**
 * The window that puts a new version of the terms of use or of the privacy
 * policy to a member, with a box to tick before going on.
 *
 * WHY. The privacy policy promises, in its section 11, to tell members before a
 * change to how long their data is kept takes effect, and version 2.2 makes
 * one. On 22/10/2026 Maude chose a window in the application, with a ticked
 * box, for the policy and the terms of use alike. Until then the window existed
 * for the sale conditions and the commission rate only.
 *
 * WHO IS ASKED is the API's decision (check-new-version answers `cgu` and
 * `privacy`); this file only says in which order and with which words.
 */

export type ATextPutAgainToTheMember = 'cgu' | 'privacy';

/** Whatever the API answered; an older API answers neither field. */
export interface WhatTheCheckSaid {
  readonly cgu?: boolean;
  readonly privacy?: boolean;
}

/**
 * The terms first, then the policy: the terms are what the member agrees to,
 * the policy is what the terms point to.
 *
 * `=== true`, not `!== false`, on purpose. The usual rule in this app is that
 * an absent new field means yes, because it is a permission. Here an absent
 * field means an API that does not know about these windows, and the answer is
 * nothing to show: asking yes would stop every member at every launch.
 */
export const theTextsToPutToTheMember = (said: WhatTheCheckSaid | null | undefined): ATextPutAgainToTheMember[] =>
  (['cgu', 'privacy'] as const).filter((text) => said?.[text] === true);

export interface TheWordsOfTheWindow {
  readonly icon: string;
  readonly title: string;
  readonly introduction: string;
  readonly checkbox: string;
  readonly button: string;
}

/**
 * Formal address throughout: these are legal texts.
 *
 * The terms are ACCEPTED, the policy is READ. The policy is information, not a
 * contract, and a box saying "I accept the privacy policy" would suggest a
 * consent it is not.
 */
export const THE_WORDS_OF_THE_WINDOW: Readonly<Record<ATextPutAgainToTheMember, TheWordsOfTheWindow>> = {
  cgu: {
    icon: 'gavel',
    title: 'LEGAL_TEXT_UPDATE.CGU.TITLE',
    introduction: 'LEGAL_TEXT_UPDATE.CGU.INTRODUCTION',
    checkbox: 'LEGAL_TEXT_UPDATE.CGU.CHECKBOX',
    button: 'BUTTONS.ACCEPT_CONTINUE',
  },
  privacy: {
    icon: 'shield_person',
    title: 'LEGAL_TEXT_UPDATE.PRIVACY.TITLE',
    introduction: 'LEGAL_TEXT_UPDATE.PRIVACY.INTRODUCTION',
    checkbox: 'LEGAL_TEXT_UPDATE.PRIVACY.CHECKBOX',
    button: 'BUTTONS.CONTINUE',
  },
};
